/**
 * PickListFilter – incremental implementation.
 *
 * Start with ALL flags false (baseline). Then turn on exactly ONE at a time:
 *   1. USE_CUSTOM_VALUE_CONTAINER → Badge (item or "X Selected") / placeholder in trigger
 *   2. USE_CUSTOM_MENU → Menu marks boundary so parent popover doesn’t close
 *   3. USE_CUSTOM_MENU_LIST → Search + checkboxes + Apply (dummy option for open)
 *   4. USE_CUSTOM_CLEAR_INDICATOR → Custom clear "X" that calls onChange([])
 *
 * When one of these breaks click/type, fix that part before enabling the next.
 */
import {
  Badge,
  Box,
  BoxProps,
  Button,
  Checkbox,
  Flex,
  Text,
  VStack,
  theme,
  useMultiStyleConfig,
} from '@chakra-ui/react'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import Select, { SelectInstance } from 'react-select'
import type { MenuProps } from 'react-select'

import { BOUNDARY_ATTRIBUTE } from '../../../hooks/useClickOutsideBoundary'
import { useIsDark } from '../../../hooks/useIsDark'
import { matchSubstring } from '../../../utils/textMatch'
import { HighlightText } from '../../HighlightText/HighlightText'
import { Icon } from '../../Icons/Icon'
import { Skeleton } from '../../SkeletonLoader'
import { Tooltip } from '../../Tooltip/Tooltip'
import {
  ClearIndicator,
  Control,
  DROPDOWN_DARK_OPTION_HOVER_BG,
  DropdownIndicator,
  IndicatorSeparator,
  IndicatorsContainer,
  Menu,
  MenuList,
  Option,
  Placeholder,
  SelectContainer,
  SelectInputOption,
  SelectInputStylesProvider,
  SingleValue,
  ValueContainer,
  rootSelectPropsContext,
  useSelectInputStyles,
} from '../../inputs/SelectInput/SelectInput.parts'

const USE_CUSTOM_VALUE_CONTAINER = true
const USE_CUSTOM_MENU = true
const USE_CUSTOM_MENU_LIST = true
const USE_CUSTOM_CLEAR_INDICATOR = true

const FILTER_EDITOR_BOUNDARY_ID = 'filter-editor-popover'

/** Dark dropdown colors (presets.dark.surface, presets.neutral.90, etc.) so portaled menu is styled when theme tokens don't resolve in portal */
const PICK_LIST_DARK_MENU = {
  menu: {
    backgroundColor: '#232526',
    border: '1px solid',
    borderColor: '#515151',
    color: '#FFFFFF',
    boxShadow: '0 4px 24px 0 rgba(0, 0, 0, 0.24)',
  },
  menuList: {
    backgroundColor: '#232526',
    borderColor: '#515151',
  },
  option: {
    backgroundColor: '#232526',
    color: '#FFFFFF',
    borderBottom: '0.5px solid',
    borderColor: '#515151',
  },
  optionHover: {
    background: DROPDOWN_DARK_OPTION_HOVER_BG,
  },
}
/** ~10 visible rows (~32px each) + footer + padding */
const PICK_LIST_DROPDOWN_MAX_HEIGHT = 400
const PICK_LIST_DROPDOWN_MIN_WIDTH = 320
const PICK_LIST_SKELETON_ROW_COUNT = 10

/** Match select menu option separator: 1px solid neutral.20 */
const listItemBorderStyles = {
  borderBottom: '1px solid',
  borderColor: 'presets.neutral.20' as const,
}

/** Checkbox 16x16 with 2px border in pick list rows */
const pickListCheckboxSx = {
  '& .chakra-checkbox__control': {
    width: '16px',
    height: '16px',
    minWidth: '16px',
    minHeight: '16px',
    borderWidth: '1px',
  },
}

/** When using custom MenuList we replace content; react-select still needs ≥1 option so the menu opens. */
const DUMMY_OPTION: SelectInputOption = {
  value: '__picklist_dummy__',
  label: ' ',
}

export type PickListFilterOption = { label: string; value: string }

export type PickListFilterProps = Omit<BoxProps, 'onChange'> & {
  value: string[]
  onChange: (values: string[]) => void
  loadOptions: (
    search: string,
    limit?: number,
    notIn?: string[],
    skip?: number,
  ) => Promise<PickListFilterOption[]>
  placeholder?: string
  isDisabled?: boolean
  /** Force-show the "Value is required." error below the input (for stories/docs). */
  showEmptyError?: boolean
  /** When false, menu is forced closed (e.g. parent filter editor popover closed). */
  isFilterEditorOpen?: boolean
  /** When set, overrides Chakra color mode for dark/light styling (e.g. from filter editor). */
  colorMode?: 'light' | 'dark'
}

const noopLoadOptions = (): Promise<PickListFilterOption[]> =>
  Promise.resolve([])

const PICK_LIST_MIN_CHARS = 1
const PICK_LIST_DEFAULT_LIMIT = 250
const PICK_LIST_SEARCH_DEBOUNCE_MS = 300
const PICK_LIST_FIRE_IMMEDIATELY = true

/**
 * We use a plain <input> (Box as="input") here instead of Chakra's <Input> so the
 * native text cursor isn't smushed. Chakra's Input wraps the real input and applies
 * styles via `& input`; that wrapper + inner 14px height can make the browser draw
 * the caret at half height. Other select inputs use react-select's default Input
 * (a single raw <input>), so their cursor is fine.
 */
const PickListValueContainer = (props: {
  children: React.ReactNode
  innerRef?: React.Ref<HTMLDivElement>
  innerProps?: Record<string, unknown>
  selectProps: {
    pickListValue?: string[]
    pickListPlaceholder?: string
    pickListSearchQuery?: string
    pickListSetSearchQuery?: (q: string) => void
    pickListOpenMenu?: () => void
    pickListMenuIsOpen?: boolean
    pickListOnEnterWhenMenuOpen?: (e: React.KeyboardEvent) => void
    pickListOnApply?: () => void
    pickListOnArrowNavigateWhenMenuOpen?: (direction: 'up' | 'down') => void
    pickListIsDark?: boolean
  }
}) => {
  const { children, innerRef, innerProps = {}, selectProps } = props
  const styles = useMultiStyleConfig('SelectInput', { variant: 'primary' })
  const appliedCount = selectProps.pickListValue?.length ?? 0
  const hasValue = appliedCount > 0
  const singleSelectedValue =
    appliedCount === 1 ? selectProps.pickListValue?.[0] : undefined
  const selectedBadgeLabel =
    singleSelectedValue != null
      ? singleSelectedValue
      : `${appliedCount} Selected`
  const asValue = selectProps.pickListSearchQuery ?? ''
  const isDark = selectProps.pickListIsDark ?? false

  const handleContainerClick = useCallback(() => {
    if (!selectProps.pickListMenuIsOpen) {
      selectProps.pickListOpenMenu?.()
    }
  }, [selectProps.pickListMenuIsOpen, selectProps.pickListOpenMenu])

  return (
    <Flex
      ref={innerRef ?? undefined}
      __css={{
        ...styles.valueContainer,
        overflow: 'visible',
        // Override theme valueContainer grid (1fr auto) so badge keeps natural width; use flex so only the input grows
        display: 'flex',
      }}
      data-part="ValueContainer"
      width="100%"
      minWidth={0}
      flex="1"
      align="center"
      gap={0}
      {...innerProps}
      onClick={handleContainerClick}
    >
      {hasValue && (
        <Tooltip
          label={
            <VStack gap="4px" alignItems="start">
              <Text>Selected:</Text>
              <Flex gap="4px" alignItems="center" flexWrap="wrap">
                {(selectProps.pickListValue ?? []).map((item, index) => (
                  <Flex key={index} gap="4px" alignItems="center">
                    <Text fontWeight="700">{item}</Text>
                    {index < (selectProps.pickListValue?.length ?? 0) - 1 && (
                      <Text>|</Text>
                    )}
                  </Flex>
                ))}
              </Flex>
            </VStack>
          }
          placement="bottom-start"
        >
          <Badge
            data-part="PickListSelectedBadge"
            data-testid="filter-pick-list-selected-badge"
            variant="simple"
            size="sm"
            bg={isDark ? 'presets.turquoise.90' : 'presets.neutral.10'}
            color={isDark ? 'presets.neutral.0' : 'presets.neutral.100'}
            {...(isDark && { _dark: { color: 'presets.neutral.0' } })}
            flexShrink={0}
            whiteSpace="nowrap"
            lineHeight="1"
            py="0"
            px="6px"
            fontWeight="500"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
            justifyContent="flex-start"
            textAlign="left"
            height="24px"
            minHeight="24px"
            alignItems="center"
            marginTop="-5px"
            marginBottom="-5px"
          >
            {selectedBadgeLabel}
          </Badge>
        </Tooltip>
      )}
      <Box
        as="input"
        type="text"
        data-part="PickListSearchInput"
        data-testid="filter-picklist-search-input"
        flex="1"
        flexBasis={0}
        width="100%"
        minWidth={0}
        py="0"
        pl={hasValue ? '8px' : 0}
        pr="8px"
        fontSize="12px"
        lineHeight="14px"
        height="14px"
        minHeight="14px"
        color="presets.turquoise.100"
        _dark={{ color: 'presets.neutral.0' }}
        placeholder={
          hasValue
            ? ''
            : (selectProps.pickListPlaceholder ?? 'Search or select value')
        }
        value={asValue}
        onChange={e => {
          const v = (e.target as HTMLInputElement).value
          selectProps.pickListSetSearchQuery?.(v)
          if (v.trim().length >= PICK_LIST_MIN_CHARS)
            selectProps.pickListOpenMenu?.()
        }}
        onKeyDown={e => {
          // Enter/Tab when menu is open: apply selections and close, stop propagation
          if (e.key === 'Enter' && selectProps.pickListMenuIsOpen) {
            selectProps.pickListOnEnterWhenMenuOpen?.(e)
            return
          }
          if (e.key === 'Tab' && selectProps.pickListMenuIsOpen) {
            e.stopPropagation()
            selectProps.pickListOnApply?.()
            return
          }
          if (
            (e.key === 'ArrowUp' || e.key === 'ArrowDown') &&
            selectProps.pickListMenuIsOpen
          ) {
            e.preventDefault()
            e.stopPropagation()
            selectProps.pickListOnArrowNavigateWhenMenuOpen?.(
              e.key === 'ArrowUp' ? 'up' : 'down',
            )
            return
          }
          if (e.key === ' ' && selectProps.pickListMenuIsOpen) {
            const activeRow = document.querySelector(
              '[data-testid="filter-pick-list-option-row"][data-keyboard-active="true"]',
            ) as HTMLElement | null
            if (!activeRow) {
              // Typing in search: Space must insert; do not fall back to first row (Select All).
              e.stopPropagation()
              return
            }
            e.preventDefault()
            e.stopPropagation()
            activeRow.dispatchEvent(
              new MouseEvent('mousedown', {
                bubbles: true,
                cancelable: true,
              }),
            )
            return
          }
          // Arrow keys open the dropdown when closed
          if (
            (e.key === 'ArrowUp' || e.key === 'ArrowDown') &&
            !selectProps.pickListMenuIsOpen
          ) {
            e.preventDefault()
            selectProps.pickListOpenMenu?.()
            return
          }
          if (
            e.key === 'Backspace' ||
            e.key === 'Delete' ||
            e.key === ' ' ||
            e.key === ','
          )
            e.stopPropagation()
        }}
        border="none"
        background="transparent"
        _focus={{ boxShadow: 'none' }}
        _focusVisible={{ outline: 'none' }}
        _placeholder={{
          color: 'presets.neutral.50',
          _dark: { color: 'whiteAlpha.600' },
        }}
      />
      <Box
        as="span"
        sx={{
          position: 'absolute',
          width: 0,
          height: 0,
          overflow: 'hidden',
          opacity: 0,
          pointerEvents: 'none',
        }}
      >
        {children}
      </Box>
    </Flex>
  )
}

const PICK_LIST_MENU_ATTR = 'data-pick-list-menu'

/** Control that does not preventDefault when clicking the search input, so the cursor is placed; dropdown still opens via wrapper onMouseDown. */
const PickListControl = (props: React.ComponentProps<typeof Control>) => {
  const { innerProps = {}, ...rest } = props
  const { onMouseDown, ...restInner } = innerProps as Record<string, unknown>
  const wrappedMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement
      if (target.closest?.('[data-testid="filter-picklist-search-input"]')) {
        return
      }
      if (target.closest?.('[data-testid="filter-pick-list-selected-badge"]')) {
        return
      }
      if (typeof onMouseDown === 'function')
        (onMouseDown as (e: React.MouseEvent) => void)(e)
    },
    [onMouseDown],
  )
  return (
    <Control
      {...rest}
      innerProps={{ ...restInner, onMouseDown: wrappedMouseDown }}
    />
  )
}

const PickListMenu = (props: MenuProps<SelectInputOption>) => (
  <Menu
    {...props}
    innerProps={
      {
        ...props.innerProps,
        'data-popover-card': true,
        [BOUNDARY_ATTRIBUTE]: FILTER_EDITOR_BOUNDARY_ID,
        [PICK_LIST_MENU_ATTR]: true,
        'data-testid': 'filter-pick-list-menu',
      } as Record<string, unknown>
    }
  />
)

export type PickListSelectProps = {
  pickListOptions: PickListFilterOption[]
  pickListSearchQuery: string
  pickListSetSearchQuery: (q: string) => void
  pickListPendingSelected: Set<string>
  pickListSetPendingSelected: React.Dispatch<React.SetStateAction<Set<string>>>
  pickListLoading: boolean
  pickListHasLoadedOnce: boolean
  pickListLoadError: string | null
  pickListOnApply: () => void
  pickListOnEnterInMenu?: (e: React.KeyboardEvent) => void
  pickListScrollPositionRef: React.MutableRefObject<number | null>
  pickListValue: string[]
  pickListNotFoundSelectedAtTop?: Set<string>
  pickListOnSelectNotFoundValue?: (value: string) => void
  pickListIsDark?: boolean
}

export const PickListMenuList = (props: {
  innerRef: React.Ref<HTMLDivElement>
  innerProps: Record<string, unknown>
  selectProps: PickListSelectProps
  children: React.ReactNode
}) => {
  const { innerRef, innerProps, selectProps } = props
  const styles = useSelectInputStyles()
  const scrollRef = useRef<HTMLDivElement>(null)
  const {
    pickListOptions: options,
    pickListSearchQuery: searchQuery,
    pickListPendingSelected: pendingSelected,
    pickListSetPendingSelected: setPendingSelected,
    pickListLoading: loading,
    pickListHasLoadedOnce: hasLoadedOnce,
    pickListLoadError: loadError,
    pickListOnApply: onApply,
    pickListScrollPositionRef: scrollPositionRef,
    pickListNotFoundSelectedAtTop: notFoundSelectedAtTop = new Set(),
    pickListOnSelectNotFoundValue: onSelectNotFoundValue,
    pickListIsDark: isDark = false,
  } = selectProps

  const rowBorderAndHoverStyles = isDark
    ? {
        bg: 'presets.dark.surface',
        borderBottom: '0.5px solid',
        borderColor: 'presets.neutral.90' as const,
        _hover: { bg: DROPDOWN_DARK_OPTION_HOVER_BG },
      }
    : { ...listItemBorderStyles, _hover: { bg: 'presets.neutral.10' } }
  const keyboardActiveRowSx = {
    '&[data-keyboard-active="true"]': {
      bg: isDark ? DROPDOWN_DARK_OPTION_HOVER_BG : 'presets.neutral.10',
    },
  } as const

  const togglePending = useCallback(
    (optValue: string) => {
      const scrollEl = scrollRef.current
      scrollPositionRef.current = scrollEl?.scrollTop ?? 0
      setPendingSelected(prev => {
        const next = new Set(prev)
        if (next.has(optValue)) next.delete(optValue)
        else next.add(optValue)
        return next
      })
    },
    [setPendingSelected, scrollPositionRef],
  )

  useLayoutEffect(() => {
    if (scrollPositionRef.current !== null && scrollRef.current) {
      scrollRef.current.scrollTop = scrollPositionRef.current
      scrollPositionRef.current = null
    }
  })

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options
    const query = searchQuery.trim().toLowerCase()
    return options
      .filter(opt => matchSubstring(opt.label, searchQuery))
      .sort((a, b) => {
        const aLower = a.label.toLowerCase()
        const bLower = b.label.toLowerCase()
        const aExact = aLower === query
        const bExact = bLower === query
        if (aExact !== bExact) return aExact ? -1 : 1
        const aPos = aLower.indexOf(query)
        const bPos = bLower.indexOf(query)
        if (aPos !== bPos) return aPos - bPos
        return aLower.localeCompare(bLower, undefined, { sensitivity: 'base' })
      })
  }, [options, searchQuery])

  const typedValue = searchQuery.trim()

  const allSelected = useMemo(
    () =>
      filteredOptions.length > 0 &&
      filteredOptions.every(opt => pendingSelected.has(opt.value)),
    [filteredOptions, pendingSelected],
  )

  const toggleSelectAll = useCallback(() => {
    const scrollEl = scrollRef.current
    scrollPositionRef.current = scrollEl?.scrollTop ?? 0
    setPendingSelected(prev => {
      if (allSelected) {
        // Clear entirely so previously applied selections not in current options are also removed
        return new Set()
      }
      const next = new Set(prev)
      for (const opt of filteredOptions) next.add(opt.value)
      return next
    })
  }, [allSelected, filteredOptions, setPendingSelected, scrollPositionRef])

  // Selected values not present in the loaded options (e.g. user-typed) — merged into list in alphabetical order below
  const notFoundSelectedValues = useMemo(() => {
    const optionValues = new Set(options.map(opt => opt.value))
    return Array.from(pendingSelected).filter(
      v =>
        !optionValues.has(v) &&
        (!searchQuery.trim() || matchSubstring(v, searchQuery)),
    )
  }, [pendingSelected, options, searchQuery])

  // Not-found values selected this session stay at top; the rest merge with options in alphabetical order (until dropdown is closed and reopened)
  const listItemsInOrder = useMemo(() => {
    const atTopNotFound = notFoundSelectedValues.filter(v =>
      notFoundSelectedAtTop.has(v),
    )
    const restNotFound = notFoundSelectedValues.filter(
      v => !notFoundSelectedAtTop.has(v),
    )
    const optionItems = filteredOptions.map(opt => ({
      type: 'option' as const,
      sortLabel: opt.label,
      opt,
    }))
    const restNotFoundItems = restNotFound.map(value => ({
      type: 'notFound' as const,
      sortLabel: value,
      value,
    }))
    const sortedRest = [...optionItems, ...restNotFoundItems]
    sortedRest.sort((a, b) =>
      a.sortLabel.localeCompare(b.sortLabel, undefined, {
        sensitivity: 'base',
      }),
    )
    const atTopItems = atTopNotFound.map(value => ({
      type: 'notFound' as const,
      sortLabel: value,
      value,
    }))
    return [...atTopItems, ...sortedRest]
  }, [filteredOptions, notFoundSelectedValues, notFoundSelectedAtTop])

  const typedValueMatchesOption = useMemo(
    () =>
      typedValue.length > 0 &&
      filteredOptions.some(
        opt =>
          opt.value.toLowerCase().trim() === typedValue.toLowerCase().trim(),
      ),
    [typedValue, filteredOptions],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        e.stopPropagation()
        selectProps.pickListOnEnterInMenu?.(e as unknown as React.KeyboardEvent)
      }
    },
    [selectProps],
  )

  return (
    <Box
      ref={innerRef}
      {...innerProps}
      __css={{
        ...styles.menuList,
        maxHeight: PICK_LIST_DROPDOWN_MAX_HEIGHT,
        overflowY: 'hidden',
        ...(isDark ? PICK_LIST_DARK_MENU.menuList : {}),
      }}
      data-part="menuList"
      onMouseDown={e => e.stopPropagation()}
      onKeyDown={handleKeyDown}
    >
      <Box
        ref={scrollRef}
        overflowY="auto"
        maxHeight={`${PICK_LIST_DROPDOWN_MAX_HEIGHT - 80}px`}
        data-part="pick-list-scroll"
        sx={{
          scrollbarWidth: 'thin',
          scrollbarColor: 'var(--chakra-colors-presets-neutral-40) transparent',
          '&::-webkit-scrollbar': {
            display: 'block',
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'var(--chakra-colors-presets-neutral-40)',
            borderRadius: '4px',
            border: '2px solid transparent',
            backgroundClip: 'padding-box',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'var(--chakra-colors-presets-neutral-60)',
          },
        }}
      >
        {loading && !hasLoadedOnce ? (
          <>
            {Array.from({ length: PICK_LIST_SKELETON_ROW_COUNT }, (_, i) => (
              <Flex
                key={`skeleton-${i}`}
                align="center"
                gap="12px"
                py="12px"
                px="12px"
                {...(isDark
                  ? {
                      borderBottom: '0.5px solid',
                      borderColor: 'presets.neutral.90',
                    }
                  : listItemBorderStyles)}
              >
                <Skeleton
                  isLoading
                  boxSize="16px"
                  flexShrink={0}
                  rounded="md"
                />
                <Skeleton isLoading height="14px" flex="1" maxW="80%" />
              </Flex>
            ))}
          </>
        ) : (
          <>
            {loadError && (
              <Box padding="12px">
                <Flex
                  py="6px"
                  px="8px"
                  align="flex-start"
                  gap="8px"
                  bg="presets.yellow.10"
                  borderRadius="4px"
                  color="#121212"
                  sx={{
                    color: '#121212',
                    // Text theme baseStyle._dark sets color: white; keep
                    // warning copy readable on the light yellow surface.
                    '& p, & [data-part="text"]': {
                      color: '#121212 !important',
                    },
                  }}
                >
                  <Icon
                    icon="WarningIcon"
                    boxSize="16px"
                    color="presets.red.50"
                    flexShrink={0}
                    alignSelf="flex-start"
                  />
                  <Text
                    fontSize="12px"
                    fontWeight="400"
                    color="#121212"
                    sx={{ color: '#121212 !important' }}
                    _dark={{ color: '#121212 !important' }}
                  >
                    Something went wrong when searching for available results.
                    Please try again.
                  </Text>
                </Flex>
              </Box>
            )}
            {filteredOptions.length > 0 && (
              <Flex
                data-part="PickListOptionRow"
                data-testid="filter-pick-list-option-row"
                sx={keyboardActiveRowSx}
                align="center"
                gap="12px"
                py="12px"
                px="12px"
                cursor="pointer"
                {...rowBorderAndHoverStyles}
                onMouseDown={e => {
                  e.preventDefault()
                  toggleSelectAll()
                }}
              >
                <Checkbox
                  isChecked={allSelected}
                  isIndeterminate={
                    !allSelected &&
                    filteredOptions.some(opt => pendingSelected.has(opt.value))
                  }
                  onChange={() => {}}
                  colorScheme="turquoise"
                  pointerEvents="none"
                  sx={pickListCheckboxSx}
                />
                <Text fontSize="12px" fontWeight="600">
                  Select All
                </Text>
              </Flex>
            )}
            {typedValue.length > 0 &&
              !typedValueMatchesOption &&
              !pendingSelected.has(typedValue) && (
                <Flex
                  data-part="PickListOptionRow"
                  data-testid="filter-pick-list-option-row"
                  sx={keyboardActiveRowSx}
                  align="center"
                  gap="12px"
                  py="12px"
                  px="12px"
                  cursor="pointer"
                  {...rowBorderAndHoverStyles}
                  onMouseDown={e => {
                    e.preventDefault()
                    onSelectNotFoundValue?.(typedValue)
                    togglePending(typedValue)
                  }}
                >
                  <Checkbox
                    isChecked={false}
                    onChange={() => {}}
                    colorScheme="turquoise"
                    pointerEvents="none"
                    sx={pickListCheckboxSx}
                  />
                  <HighlightText
                    text={typedValue}
                    searchTerm={searchQuery}
                    matchMode="substring"
                    fontSize="12px"
                    fontWeight="600"
                  />
                </Flex>
              )}
            {listItemsInOrder.map(item =>
              item.type === 'option' ? (
                <Flex
                  key={item.opt.value}
                  data-part="PickListOptionRow"
                  data-testid="filter-pick-list-option-row"
                  sx={keyboardActiveRowSx}
                  align="center"
                  gap="12px"
                  py="12px"
                  px="12px"
                  cursor="pointer"
                  {...rowBorderAndHoverStyles}
                  onMouseDown={e => {
                    e.preventDefault()
                    togglePending(item.opt.value)
                  }}
                >
                  <Checkbox
                    isChecked={pendingSelected.has(item.opt.value)}
                    onChange={() => {}}
                    colorScheme="turquoise"
                    pointerEvents="none"
                    sx={pickListCheckboxSx}
                  />
                  <HighlightText
                    text={item.opt.label}
                    searchTerm={searchQuery}
                    matchMode="substring"
                    fontSize="12px"
                    fontWeight="600"
                  />
                </Flex>
              ) : (
                <Flex
                  key={`not-found-${item.value}`}
                  data-part="PickListOptionRow"
                  data-testid="filter-pick-list-option-row"
                  sx={keyboardActiveRowSx}
                  align="center"
                  gap="12px"
                  py="12px"
                  px="12px"
                  cursor="pointer"
                  {...rowBorderAndHoverStyles}
                  onMouseDown={e => {
                    e.preventDefault()
                    togglePending(item.value)
                  }}
                >
                  <Checkbox
                    isChecked={pendingSelected.has(item.value)}
                    onChange={() => {}}
                    colorScheme="turquoise"
                    pointerEvents="none"
                    sx={pickListCheckboxSx}
                  />
                  <HighlightText
                    text={item.value}
                    searchTerm={searchQuery}
                    matchMode="substring"
                    fontSize="12px"
                    fontWeight="600"
                  />
                </Flex>
              ),
            )}
          </>
        )}
      </Box>
      {(hasLoadedOnce || !loading) && !loadError && (
        <Flex
          borderTopWidth="1px"
          borderColor={isDark ? 'presets.neutral.90' : 'presets.neutral.20'}
          marginTop="-1px"
          py="4px"
          px="12px"
          justifyContent="flex-start"
        >
          <Button
            size="sm"
            variant="tertiary"
            fontWeight="600"
            leftIcon={<Icon icon="CheckIcon" boxSize="14px" />}
            onMouseDown={e => {
              e.preventDefault()
              onApply()
            }}
          >
            Done
          </Button>
        </Flex>
      )}
    </Box>
  )
}

const PickListClearIndicator = ({
  innerProps,
  selectProps,
}: {
  innerProps: Record<string, unknown>
  selectProps: { pickListValue?: string[]; pickListOnClear?: () => void }
}) => {
  const count = selectProps.pickListValue?.length ?? 0
  if (count === 0) return null
  return (
    <Flex
      {...innerProps}
      data-part="ClearIndicator"
      onClick={e => {
        e.stopPropagation()
        selectProps.pickListOnClear?.()
      }}
    >
      <Icon
        icon="XIcon"
        backgroundColor="presets.pink.70"
        color="presets.neutral.0"
        rounded="full"
        padding="2px"
        boxSize="14px"
        mr="4px"
      />
    </Flex>
  )
}

export const PickListFilter = ({
  value,
  onChange,
  loadOptions: loadOptionsProp,
  placeholder = 'Search or select value',
  isDisabled = false,
  showEmptyError = false,
  isFilterEditorOpen = true,
  colorMode: colorModeOverride,
  ...props
}: PickListFilterProps) => {
  const isDark = useIsDark(colorModeOverride)
  const rawStyles = useMultiStyleConfig('SelectInput', { variant: 'primary' })
  const styles = useMemo(() => {
    if (!isDark) return rawStyles
    const merged: Record<string, Record<string, unknown>> = {}
    for (const key of Object.keys(rawStyles)) {
      const part = rawStyles[key] as Record<string, unknown> | undefined
      if (part && typeof part === 'object' && part._dark) {
        const { _dark, ...rest } = part
        merged[key] = { ...rest, ...(_dark as Record<string, unknown>) }
      } else {
        merged[key] = part ?? {}
      }
    }
    return merged as typeof rawStyles
  }, [rawStyles, isDark])
  const selectRef = useRef<SelectInstance<SelectInputOption>>(null)
  const [menuIsOpen, setMenuIsOpen] = useState(false)
  const [options, setOptions] = useState<SelectInputOption[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [pendingSelected, setPendingSelected] = useState<Set<string>>(new Set())
  const [emptyFieldError, setEmptyFieldError] = useState(false)
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [notFoundSelectedAtTop, setNotFoundSelectedAtTop] = useState<
    Set<string>
  >(new Set())
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scrollPositionRef = useRef<number | null>(null)

  const loadOptions =
    typeof loadOptionsProp === 'function' ? loadOptionsProp : noopLoadOptions

  const loadOptionsDebounced = useCallback(
    (query: string) => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
      searchDebounceRef.current = setTimeout(() => {
        setLoading(true)
        setLoadError(null)
        loadOptions(query, PICK_LIST_DEFAULT_LIMIT, undefined, 0)
          .then(opts => {
            const sorted = [...opts].sort((a, b) =>
              a.label.localeCompare(b.label, undefined, {
                sensitivity: 'base',
              }),
            )
            setOptions(sorted)
            setHasLoadedOnce(true)
            setLoadError(null)
          })
          .catch(err => {
            setLoadError(err?.message ?? 'Something went wrong')
            setHasLoadedOnce(true)
          })
          .finally(() => {
            setLoading(false)
            searchDebounceRef.current = null
          })
      }, PICK_LIST_SEARCH_DEBOUNCE_MS)
    },
    [loadOptions],
  )

  const handleMenuOpen = useCallback(() => {
    if (menuIsOpen) return
    setMenuIsOpen(true)
    setPendingSelected(new Set(value))
    setLoadError(null)
    const q = searchQuery.trim()
    const shouldLoad =
      PICK_LIST_FIRE_IMMEDIATELY || q.length >= PICK_LIST_MIN_CHARS
    if (shouldLoad) {
      setLoading(true)
      loadOptions(
        PICK_LIST_FIRE_IMMEDIATELY ? '' : q,
        PICK_LIST_DEFAULT_LIMIT,
        undefined,
        0,
      )
        .then(opts => {
          const sorted = [...opts].sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
          )
          setOptions(sorted)
          setHasLoadedOnce(true)
          setLoadError(null)
        })
        .catch(err => {
          setLoadError(err?.message ?? 'Something went wrong')
          setHasLoadedOnce(true)
        })
        .finally(() => setLoading(false))
    } else {
      setOptions([])
      setLoading(false)
    }
  }, [menuIsOpen, value, loadOptions, searchQuery])

  const handleMenuClose = useCallback((force = false) => {
    if (
      !force &&
      document.activeElement?.closest?.(
        '[data-testid="filter-picklist-search-input"]',
      )
    ) {
      return
    }
    setMenuIsOpen(false)
    setHasLoadedOnce(false)
    setLoadError(null)
    setNotFoundSelectedAtTop(new Set())
    if (force) setSearchQuery('')
    setPendingSelected(new Set())
  }, [])

  useEffect(() => {
    if (pendingSelected.size > 0) setEmptyFieldError(false)
  }, [pendingSelected])

  useEffect(() => {
    if (menuIsOpen) return
    document
      .querySelectorAll('[data-testid="filter-pick-list-option-row"]')
      .forEach(row => row.removeAttribute('data-keyboard-active'))
  }, [menuIsOpen])

  // Close menu when parent filter editor popover closes so portaled menu doesn't stay open
  useEffect(() => {
    if (!isFilterEditorOpen) {
      setMenuIsOpen(false)
      setHasLoadedOnce(false)
      setLoadError(null)
      setSearchQuery('')
      setPendingSelected(new Set())
      setNotFoundSelectedAtTop(new Set())
    }
  }, [isFilterEditorOpen])

  // Close menu when clicking outside (control or portaled menu)
  useEffect(() => {
    if (!menuIsOpen) return
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        document
          .querySelector('[data-testid="filter-pick-list-filter"]')
          ?.contains(target)
      )
        return
      if (
        document
          .querySelector('[data-testid="filter-pick-list-menu"]')
          ?.contains(target)
      )
        return
      handleMenuClose(true)
    }
    document.addEventListener('mousedown', onMouseDown, true)
    return () => document.removeEventListener('mousedown', onMouseDown, true)
  }, [menuIsOpen, handleMenuClose])

  // Re-fetch when user types while menu is open (skip when closed to avoid mount/re-render fires)
  const prevSearchRef = useRef(searchQuery)
  useEffect(() => {
    if (!menuIsOpen) return
    // Skip if searchQuery hasn't actually changed (avoids re-fire from loadOptionsDebounced identity change)
    if (prevSearchRef.current === searchQuery) return
    prevSearchRef.current = searchQuery
    if (
      !PICK_LIST_FIRE_IMMEDIATELY &&
      searchQuery.trim().length < PICK_LIST_MIN_CHARS
    )
      return
    loadOptionsDebounced(searchQuery.trim())
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    }
  }, [menuIsOpen, searchQuery, loadOptionsDebounced])

  const handleApply = useCallback(() => {
    const next = Array.from(pendingSelected)
    setEmptyFieldError(false)
    onChange(next)
    handleMenuClose(true)
    selectRef.current?.blur()
  }, [pendingSelected, onChange, handleMenuClose])

  const handleEnterWhenMenuOpen = useCallback(
    (e: React.KeyboardEvent) => {
      e.preventDefault()
      e.stopPropagation()
      handleApply()
    },
    [handleApply],
  )

  const handleClear = useCallback(() => {
    setEmptyFieldError(false)
    onChange([])
    handleMenuClose()
    selectRef.current?.blur()
  }, [onChange, handleMenuClose])

  const handleArrowNavigateWhenMenuOpen = useCallback(
    (direction: 'up' | 'down') => {
      const rows = Array.from(
        document.querySelectorAll(
          '[data-testid="filter-pick-list-option-row"]',
        ),
      ) as HTMLElement[]
      if (rows.length === 0) return

      const currentIndex = rows.findIndex(
        row => row.getAttribute('data-keyboard-active') === 'true',
      )
      const baseIndex =
        currentIndex === -1
          ? direction === 'down'
            ? -1
            : rows.length
          : currentIndex
      const nextIndex =
        direction === 'down'
          ? (baseIndex + 1) % rows.length
          : (baseIndex - 1 + rows.length) % rows.length

      rows.forEach(row => row.removeAttribute('data-keyboard-active'))
      rows[nextIndex]?.setAttribute('data-keyboard-active', 'true')
      if (rows[nextIndex] && 'scrollIntoView' in rows[nextIndex]) {
        rows[nextIndex].scrollIntoView({ block: 'nearest' })
      }
    },
    [],
  )

  const pickListSelectProps = {
    pickListValue: value,
    pickListOnChange: onChange,
    pickListOnClear: handleClear,
    pickListPlaceholder: placeholder,
    pickListLoadOptions: loadOptions,
    pickListPendingSelected: pendingSelected,
    pickListSetPendingSelected: setPendingSelected,
    pickListOptions: options,
    pickListSearchQuery: searchQuery,
    pickListSetSearchQuery: setSearchQuery,
    pickListNotFoundSelectedAtTop: notFoundSelectedAtTop,
    pickListOnSelectNotFoundValue: (v: string) =>
      setNotFoundSelectedAtTop(prev => new Set(prev).add(v)),
    pickListLoading: loading,
    pickListHasLoadedOnce: hasLoadedOnce,
    pickListLoadError: loadError,
    pickListOnApply: handleApply,
    pickListOnEnterInMenu: handleEnterWhenMenuOpen,
    pickListOnArrowNavigateWhenMenuOpen: handleArrowNavigateWhenMenuOpen,
    pickListOpenMenu: () => selectRef.current?.openMenu?.('first'),
    pickListScrollPositionRef: scrollPositionRef,
    pickListIsDark: isDark,
    pickListMenuIsOpen: menuIsOpen,
    pickListOnEnterWhenMenuOpen: handleEnterWhenMenuOpen,
  }

  const selectOptions = USE_CUSTOM_MENU_LIST ? [DUMMY_OPTION] : options
  const selectValue = null as SelectInputOption | null

  const handleWrapperMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (
        USE_CUSTOM_MENU_LIST &&
        !menuIsOpen &&
        !(e.target as HTMLElement).closest('[data-part="ClearIndicator"]')
      ) {
        selectRef.current?.openMenu?.('first')
      }
    },
    [menuIsOpen],
  )

  return (
    <Box
      flex="1"
      minWidth={0}
      className="react-select react-select-pick-list"
      data-pick-list-filter
      data-testid="filter-pick-list-filter"
      onMouseDown={handleWrapperMouseDown}
      {...props}
    >
      <VStack align="stretch" gap="4px" alignItems="stretch">
        <rootSelectPropsContext.Provider value={{ maxVisibleOptions: 8 }}>
          <SelectInputStylesProvider value={{ ...styles, rootProps: props }}>
            <Select<SelectInputOption>
              ref={selectRef}
              captureMenuScroll={false}
              options={selectOptions}
              value={selectValue}
              onChange={() => {}}
              placeholder={placeholder}
              menuIsOpen={menuIsOpen}
              onMenuOpen={handleMenuOpen}
              onMenuClose={handleMenuClose}
              isDisabled={isDisabled}
              isClearable={USE_CUSTOM_CLEAR_INDICATOR ? false : true}
              isSearchable={!USE_CUSTOM_MENU_LIST}
              openMenuOnFocus={false}
              filterOption={USE_CUSTOM_MENU_LIST ? () => true : undefined}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              menuPlacement="auto"
              blurInputOnSelect={false}
              {...(USE_CUSTOM_VALUE_CONTAINER ||
              USE_CUSTOM_MENU_LIST ||
              USE_CUSTOM_CLEAR_INDICATOR
                ? pickListSelectProps
                : {})}
              components={{
                SelectContainer,
                Control: PickListControl,
                ValueContainer: USE_CUSTOM_VALUE_CONTAINER
                  ? (PickListValueContainer as typeof ValueContainer)
                  : ValueContainer,
                Placeholder: USE_CUSTOM_VALUE_CONTAINER
                  ? () => null
                  : Placeholder,
                SingleValue: USE_CUSTOM_VALUE_CONTAINER
                  ? () => null
                  : SingleValue,
                DropdownIndicator,
                IndicatorSeparator,
                IndicatorsContainer,
                ClearIndicator: USE_CUSTOM_CLEAR_INDICATOR
                  ? (PickListClearIndicator as any)
                  : ClearIndicator,
                Menu: USE_CUSTOM_MENU ? PickListMenu : Menu,
                MenuList: USE_CUSTOM_MENU_LIST
                  ? (PickListMenuList as any)
                  : MenuList,
                Option,
              }}
              styles={{
                menuPortal: (base: object) => ({
                  ...base,
                  zIndex: theme.zIndices.popover,
                  minWidth: PICK_LIST_DROPDOWN_MIN_WIDTH,
                }),
                menu: (base: object) => ({
                  ...base,
                  minWidth: PICK_LIST_DROPDOWN_MIN_WIDTH,
                  ...(isDark ? PICK_LIST_DARK_MENU.menu : {}),
                }),
                menuList: (base: object) => ({
                  ...base,
                  ...(isDark ? PICK_LIST_DARK_MENU.menuList : {}),
                }),
                option: (base: object, state: { isFocused: boolean }) => ({
                  ...base,
                  ...(isDark
                    ? {
                        ...PICK_LIST_DARK_MENU.option,
                        ...(state.isFocused
                          ? PICK_LIST_DARK_MENU.optionHover
                          : {}),
                      }
                    : {}),
                }),
                input: (base: object) => ({
                  ...base,
                  margin: 0,
                  padding: 0,
                  gridArea: '1/1/2/3',
                }),
              }}
            />
          </SelectInputStylesProvider>
        </rootSelectPropsContext.Provider>
        {(emptyFieldError || showEmptyError) && (
          <Text fontSize="12px" fontWeight="400" color="presets.red.50">
            Value is required.
          </Text>
        )}
      </VStack>
    </Box>
  )
}

PickListFilter.displayName = 'PickListFilter'
